import { ReactNode, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { useSnapshot } from 'valtio';

import * as authStore from '@cartwhl/common/stores/authStore';
import { getHomepageUrl } from '@cartwhl/common/utils';
import Text from '@cartwhl/ui/base/TextNew';
import CrossIcon from '@cartwhl/ui/icons/CrossIcon';
import Button from '@cartwhl/ui/input/Button';

const HOMEPAGE_APPS_NOTE_DISMISSED = 'homepage-apps-note-dismissed';
const HOMEPAGE_APPS_NOTE_DEV_TTL_MS = 1000 * 60 * 30;

interface HomepageAppsNoteLinkProps {
  href: string;
  children: ReactNode;
}

function getHomepageAppsNoteDismissedAt() {
  if (typeof sessionStorage === 'undefined') return null;

  const rawDismissedAt = sessionStorage.getItem(HOMEPAGE_APPS_NOTE_DISMISSED);
  if (!rawDismissedAt) return null;

  const dismissedAt = Number(rawDismissedAt);
  return Number.isFinite(dismissedAt) ? dismissedAt : null;
}

function getHomepageAppsNoteDismissed() {
  const dismissedAt = getHomepageAppsNoteDismissedAt();
  if (dismissedAt === null) return false;

  if (!import.meta.env.DEV) {
    return true;
  }

  if (Date.now() - dismissedAt > HOMEPAGE_APPS_NOTE_DEV_TTL_MS) {
    sessionStorage.removeItem(HOMEPAGE_APPS_NOTE_DISMISSED);
    return false;
  }

  return true;
}

function NotificationsIcon({ className }: { className?: string }) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M10 2.5C7.79086 2.5 6 4.29086 6 6.5V8.85131C6 9.33551 5.82439 9.80328 5.50579 10.1679L4 11.8906V12.5H16V11.8906L14.4942 10.1679C14.1756 9.80328 14 9.33551 14 8.85131V6.5C14 4.29086 12.2091 2.5 10 2.5Z"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M8.75 15C8.95444 15.5831 9.51096 16 10.125 16C10.739 16 11.2956 15.5831 11.5 15"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function HomepageAppsNote() {
  const { authStatus } = useSnapshot(authStore.state);
  const [homepageAppsNoteDismissed, setHomepageAppsNoteDismissed] = useState(
    () => getHomepageAppsNoteDismissed(),
  );

  const showHomepageAppsNote =
    authStatus === 'user' && !homepageAppsNoteDismissed;

  useEffect(() => {
    if (!import.meta.env.DEV || !homepageAppsNoteDismissed) {
      return;
    }

    const dismissedAt = getHomepageAppsNoteDismissedAt();
    if (dismissedAt === null) {
      return;
    }

    const remainingMs =
      HOMEPAGE_APPS_NOTE_DEV_TTL_MS - (Date.now() - dismissedAt);

    if (remainingMs <= 0) {
      sessionStorage.removeItem(HOMEPAGE_APPS_NOTE_DISMISSED);
      setHomepageAppsNoteDismissed(false);
      return;
    }

    const timeoutId = window.setTimeout(() => {
      sessionStorage.removeItem(HOMEPAGE_APPS_NOTE_DISMISSED);
      setHomepageAppsNoteDismissed(false);
    }, remainingMs);

    return () => window.clearTimeout(timeoutId);
  }, [homepageAppsNoteDismissed]);

  const dismissHomepageAppsNote = () => {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(HOMEPAGE_APPS_NOTE_DISMISSED, String(Date.now()));
    }
    setHomepageAppsNoteDismissed(true);
  };

  if (!showHomepageAppsNote) {
    return null;
  }

  return (
    <>
      <div className="pb-40 lg:pb-24" />
      <div
        className="fixed inset-x-2.5 bottom-4 z-40 flex justify-center lg:inset-x-5"
        role="note"
      >
        <div className="relative w-full max-w-[820px]">
          <div className="bg-yellow text-primary flex items-start gap-4 overflow-hidden rounded-2xl px-4 py-4 pr-10 shadow-[0px_4px_8px_3px_rgba(0,0,0,0.08),0px_1px_3px_0px_rgba(0,0,0,0.08)] lg:pr-4">
            <NotificationsIcon className="mt-0.5 h-5 w-5 shrink-0" />

            <Text
              as="p"
              token="14/medium"
              className="min-w-0 flex-1 text-center"
            >
              New apps are live.
              <br className="lg:hidden" />{' '}
              <span className="font-bold">
                <Link
                  to={`/product/mascot`}
                  className="text-blue hover:text-blue-60 underline transition-colors"
                >
                  Mascot
                </Link>
                {', and '}
                <Link
                  to={`/product/cartographer`}
                  className="text-blue hover:text-blue-60 underline transition-colors"
                >
                  Cartographer
                </Link>
                {/* {', and '}
                <Link
                  to={`/product/hermes-smart-in-between`}
                  className="text-blue hover:text-blue-60 underline transition-colors"
                >
                  Hermes
                </Link> */}
              </span>{' '}
              are now available as standalone apps.
              <br />
              Find them in the Product menu above and launch from their pages.{' '}
              Canvas still has everything you know!
            </Text>
          </div>

          <Button
            type="button"
            variant="secondary"
            size="sm"
            icon={<CrossIcon />}
            onClick={dismissHomepageAppsNote}
            className="absolute right-[-8px] top-[-9px]"
            aria-label="Dismiss app announcement"
          />
        </div>
      </div>
    </>
  );
}
